"use client";

import { useState, useEffect, useRef } from "react";
import { differenceInCalendarDays, format } from "date-fns";
import {
  CalendarIcon,
  Clock,
  Calendar as CalendarDaysIcon,
  TrendingUp,
  CalendarDays,
} from "lucide-react";
import { cn } from "@/shared/lib/utils";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverContentNoPortal,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { DateRange } from "react-day-picker";

export type MonthRange = {
  start: Date;
  end: Date;
};

interface Props {
  value?: MonthRange;
  onChange: (value: MonthRange | undefined) => void;
  className?: string;
}

// Helper function to get current month range (1st of current month to current date)
const getCurrentMonthRange = (): MonthRange => {
  const now = new Date();
  const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  // Set time to start of day
  firstDayOfMonth.setHours(0, 0, 0, 0);
  today.setHours(23, 59, 59, 999);

  return {
    start: firstDayOfMonth,
    end: today,
  };
};

// Helper function to get today's date range
const getTodayRange = (): MonthRange => {
  const today = new Date();
  const startOfDay = new Date(today);
  startOfDay.setHours(0, 0, 0, 0);

  const endOfDay = new Date(today);
  endOfDay.setHours(23, 59, 59, 999);

  return {
    start: startOfDay,
    end: endOfDay,
  };
};

// Helper function to get date range for quick selectors
const getDateRange = (monthsAgo: number): MonthRange => {
  const today = new Date();
  today.setHours(23, 59, 59, 999);

  const startDate = new Date(today);
  startDate.setMonth(today.getMonth() - monthsAgo);
  startDate.setHours(0, 0, 0, 0);

  return {
    start: startDate,
    end: today,
  };
};

// Helper function to get last month range (changed to default)
const getLastMonthRange = (): MonthRange => {
  const now = new Date();

  // First day of last month
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  start.setHours(0, 0, 0, 0);

  // Last day of last month
  const end = new Date(now.getFullYear(), now.getMonth(), 0);
  end.setHours(23, 59, 59, 999);

  return { start, end };
};

export function MonthRangeFilter({ value, onChange, className }: Props) {
  const [open, setOpen] = useState(false);
  const [tempDate, setTempDate] = useState<DateRange | undefined>();
  const [activeQuickSelect, setActiveQuickSelect] = useState<
    "today" | "1" | "6" | "12" | null
  >("1");
  const [displayedMonth, setDisplayedMonth] = useState<Date>(new Date());
  const hasInitialized = useRef(false);

  // Set default value to last month if no value is provided (only once)
  useEffect(() => {
    if (!hasInitialized.current && !value) {
      const defaultRange = getLastMonthRange();
      onChange(defaultRange);
      hasInitialized.current = true;
    }
  }, [value, onChange]);

  // Sync tempDate with value
  useEffect(() => {
    if (value) {
      setTempDate({
        from: value.start,
        to: value.end,
      });
    }
  }, [value]);

  // When popover opens, show current month
  useEffect(() => {
    if (open) {
      setDisplayedMonth(new Date());
    }
  }, [open]);

  const handleDaySelect = (newRange: DateRange | undefined) => {
    setTempDate(newRange);
  };

  const handleSave = () => {
    if (tempDate?.from && tempDate?.to) {
      // Set time to start and end of day
      const startDate = new Date(tempDate.from);
      startDate.setHours(0, 0, 0, 0);

      const endDate = new Date(tempDate.to);
      endDate.setHours(23, 59, 59, 999);

      onChange({
        start: startDate,
        end: endDate,
      });
      setActiveQuickSelect(null); // Clear active state for custom range
    }
    setOpen(false);
  };

  const handleQuickSelect = (type: "today" | "1" | "6" | "12") => {
    let range: MonthRange;

    switch (type) {
      case "today":
        range = getTodayRange();
        break;

      case "1":
        range = getLastMonthRange();
        break;

      case "6":
      case "12":
        // ✅ ROLLING RANGE
        range = getDateRange(parseInt(type));
        break;
    }

    setTempDate({
      from: range.start,
      to: range.end,
    });

    onChange(range);
    setActiveQuickSelect(type);
    setOpen(false);
  };

  const displayValue = value || getCurrentMonthRange();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "flex h-[2.625rem] items-center justify-between rounded-xl bg-gradient-to-br from-white to-gray-50 px-2 py-2 text-sm font-semibold transition-all duration-200",
            "hover:border-green-700 hover:from-green-50 hover:to-orange-50 hover:shadow-lg",
            "focus:outline-none focus:ring-2 focus:ring-green-800 focus:ring-offset-2",
            "w-[300px] gap-3 text-left shadow-sm",
            className
          )}
        >
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            <CalendarDays className="h-4 w-4 text-green-700 flex-shrink-0" />
            <span className="text-gray-800 truncate font-medium">
              {format(displayValue.start, "d MMM yyyy")} -{" "}
              {format(displayValue.end, "d MMM yyyy")}
            </span>
          </div>
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="w-auto p-0 shadow-2xl border-2 border-gray-200 rounded-xl overflow-hidden"
        align="end"
        side="bottom"
        // sideOffset={8}
        // avoidCollisions={false}
      >
        {/* Header */}
        {/* <div className="px-5 py-4 border-b-2 border-gray-100 bg-gradient-to-r from-green-700 to-green-500">
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 bg-white/20 rounded-lg backdrop-blur-sm">
              <CalendarDaysIcon className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="text-base font-bold text-white">
                Select Date Range
              </h3>
              <p className="text-xs text-white/90 mt-0.5">
                Choose custom dates or use quick options
              </p>
            </div>
          </div>
        </div> */}

        <div className="flex">
          {/* Calendar Section */}
          <div className="flex flex-col p-5 bg-white min-w-[320px]">
            <Calendar
              classNames={{
                week: "flex",
                day: "flex-1",
                month: "flex flex-col w-full gap-5",
              }}
              mode="range"
              month={displayedMonth}
              onMonthChange={setDisplayedMonth}
              selected={tempDate}
              onSelect={handleDaySelect}
              numberOfMonths={1}
              captionLayout="dropdown"
              toDate={new Date()} // Disable future dates
            />

            <div className="flex items-center justify-between pt-4 mt-4 border-t-2 border-gray-100">
              <div className="text-xs text-gray-600 font-medium">
                {tempDate?.from && tempDate?.to ? (
                  <span className="flex items-center gap-1.5 bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-200">
                    <CalendarIcon className="h-3.5 w-3.5 text-blue-600" />
                    <span className="text-blue-700 font-semibold">
                      {differenceInCalendarDays(tempDate.to, tempDate.from) + 1}{" "}
                      days
                    </span>
                  </span>
                ) : (
                  <span className="text-gray-400 italic">Select dates</span>
                )}
              </div>
              <Button
                onClick={handleSave}
                className="px-5 py-2 bg-gradient-to-r from-red-600 to-red-700 text-white hover:from-red-700 hover:to-red-800 transition-all shadow-lg hover:shadow-xl font-semibold"
                size="sm"
                disabled={!tempDate?.from || !tempDate?.to}
              >
                Apply Range
              </Button>
            </div>
          </div>

          {/* Quick Selectors */}
          <div className="flex flex-col gap-2.5 p-5 bg-gradient-to-br from-gray-50 to-gray-100 border-l-2 border-gray-200 min-w-[180px]">
            <div className="mb-1 pb-2 border-b border-gray-300">
              <p className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5 text-red-600" />
                Quick Select
              </p>
            </div>

            {/* Today Button */}
            <Button
              onClick={() => handleQuickSelect("today")}
              variant={activeQuickSelect === "today" ? "default" : "outline"}
              className={cn(
                "w-full justify-start gap-2.5 transition-all duration-200 text-sm font-semibold h-11 rounded-lg",
                activeQuickSelect === "today"
                  ? "bg-gradient-to-r from-red-600 to-red-700 text-white hover:from-red-700 hover:to-red-800 border-red-600 shadow-md hover:shadow-lg scale-[1.02]"
                  : "bg-white hover:bg-red-50 text-gray-700 hover:text-red-700 hover:border-red-400 shadow-sm hover:shadow-md"
              )}
            >
              <CalendarDaysIcon className="h-4 w-4" />
              Today
            </Button>

            {/* Last Month Button */}
            <Button
              onClick={() => handleQuickSelect("1")}
              variant={activeQuickSelect === "1" ? "default" : "outline"}
              className={cn(
                "w-full justify-start gap-2.5 transition-all duration-200 text-sm font-semibold h-11 rounded-lg",
                activeQuickSelect === "1"
                  ? "bg-gradient-to-r from-red-600 to-red-700 text-white hover:from-red-700 hover:to-red-800 border-red-600 shadow-md hover:shadow-lg scale-[1.02]"
                  : "bg-white hover:bg-red-50 text-gray-700 hover:text-red-700 hover:border-red-400 shadow-sm hover:shadow-md"
              )}
            >
              <Clock className="h-4 w-4" />
              Last Month
            </Button>

            {/* 6 Months Button */}
            <Button
              onClick={() => handleQuickSelect("6")}
              variant={activeQuickSelect === "6" ? "default" : "outline"}
              className={cn(
                "w-full justify-start gap-2.5 transition-all duration-200 text-sm font-semibold h-11 rounded-lg",
                activeQuickSelect === "6"
                  ? "bg-gradient-to-r from-red-600 to-red-700 text-white hover:from-red-700 hover:to-red-800 border-red-600 shadow-md hover:shadow-lg scale-[1.02]"
                  : "bg-white hover:bg-red-50 text-gray-700 hover:text-red-700 hover:border-red-400 shadow-sm hover:shadow-md"
              )}
            >
              <CalendarDaysIcon className="h-4 w-4" />6 Months
            </Button>

            {/* 12 Months Button */}
            <Button
              onClick={() => handleQuickSelect("12")}
              variant={activeQuickSelect === "12" ? "default" : "outline"}
              className={cn(
                "w-full justify-start gap-2.5 transition-all duration-200 text-sm font-semibold h-11 rounded-lg",
                activeQuickSelect === "12"
                  ? "bg-gradient-to-r from-red-600 to-red-700 text-white hover:from-red-700 hover:to-red-800 border-red-600 shadow-md hover:shadow-lg scale-[1.02]"
                  : "bg-white hover:bg-red-50 text-gray-700 hover:text-red-700 hover:border-red-400 shadow-sm hover:shadow-md"
              )}
            >
              <TrendingUp className="h-4 w-4" />
              12 Months
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
